import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faCheckCircle, faStar, faArrowLeft, faSpinner 
} from '@fortawesome/free-solid-svg-icons'
import { CoordinatorUpgradeService } from '../services/coordinatorUpgradeService'
import { NotificationIntegration } from '../services/notificationIntegration'

export default function CoordinatorUpgradeSuccess() {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [paymentVerified, setPaymentVerified] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    verifyPayment()
  }, [])

  const verifyPayment = async () => {
    try {
      setLoading(true)
      
      // Get payment reference from URL parameters
      const paymentId = searchParams.get('pf_payment_id')
      const paymentReference = searchParams.get('m_payment_id')
      
      if (paymentReference) {
        // In a real implementation, you would verify the payment with PayFast
        // For now, we'll simulate a successful verification
        setPaymentVerified(true)
        
        // Trigger coordinator upgrade notification
        NotificationIntegration.onCoordinatorUpgrade(1)
        
        // Trigger payment success notification
        NotificationIntegration.onPaymentSuccess(300, 'Coordinator Upgrade')
      } else {
        setError('Payment reference not found')
      }
    } catch (error) {
      console.error('Error verifying payment:', error)
      setError('Failed to verify payment')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 max-w-md w-full text-center">
          <FontAwesomeIcon icon={faSpinner} className="h-12 w-12 animate-spin text-orange-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">✕</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Verification Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <Link
              to="/dashboard"
              className="block w-full bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Return to Dashboard
            </Link>
            <Link
              to="/contact"
              className="block w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="app-header">
        <div className="flex items-center justify-between max-w-md mx-auto">
          <Link to="/dashboard" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="h-6 w-6" />
          </Link>
          <h1 className="app-header-title">Payment Successful</h1>
          <div className="w-6"></div>
        </div>
      </header>

      {/* Success Content */}
      <main className="p-4 max-w-md mx-auto">
        <div className="bg-white rounded-lg p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FontAwesomeIcon icon={faCheckCircle} className="h-12 w-12 text-green-600" />
          </div>

          {/* Success Message */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome, Coordinator!
          </h2>
          <p className="text-gray-600 mb-6">
            Your payment has been processed successfully. You are now a GOMTB Event Coordinator with 1 event creation credit.
          </p>

          {/* Benefits */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center mb-3">
              <FontAwesomeIcon icon={faStar} className="h-6 w-6 text-orange-600 mr-2" />
              <span className="font-semibold text-orange-800">Coordinator Benefits</span>
            </div>
            <ul className="text-sm text-orange-700 space-y-2 text-left">
              <li className="flex items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>Create and manage mountain biking events</span>
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>Access to participant management tools</span>
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>Event registration and payment tracking</span>
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>Professional coordinator badge</span>
              </li>
            </ul>
          </div>

          {/* Credit Information */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-green-800 mb-2">Event Creation Credit</h3>
            <p className="text-sm text-green-700">
              You now have <strong>1 event creation credit</strong> available. Use it to create your first event and start building the mountain biking community!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Link
              to="/coordinator/events"
              className="block w-full bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium"
            >
              Create Your First Event
            </Link>
            <Link
              to="/dashboard"
              className="block w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>

          {/* Additional Information */}
          <div className="mt-6 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
            <p className="font-semibold mb-1">Need More Credits?</p>
            <p>Each additional event requires 1 credit (R300). You can purchase more credits anytime from your dashboard.</p>
          </div>
        </div>
      </main>
    </div>
  )
} 